import React, { useEffect, useRef, useState } from 'react'
import { HeartIcon as HeartOutline } from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { getNewsLikers, toggleNewsLike } from '../../services/informativo'
import type { NewsLiker, NewsLikeSummary } from '../../types/informativo'
import { Tooltip } from '../ui/tooltip'

type Props = {
  newsId: string
  summary: NewsLikeSummary
  onChanged: (newsId: string, next: NewsLikeSummary) => void
  className?: string
}

const NewsLikeButton: React.FC<Props> = ({
  newsId,
  summary,
  onChanged,
  className,
}) => {
  const [busy, setBusy] = useState(false)
  const [likers, setLikers] = useState<NewsLiker[] | null>(null)
  const [loadingLikers, setLoadingLikers] = useState(false)
  const likersRequestId = useRef(0)

  // Invalida cache de nomes quando a contagem muda (curtir/descurtir).
  useEffect(() => {
    setLikers(null)
  }, [summary.count, summary.likedByMe, newsId])

  const loadLikers = async () => {
    if (!newsId || summary.count <= 0 || loadingLikers) return
    if (likers !== null) return

    const requestId = ++likersRequestId.current
    setLoadingLikers(true)
    try {
      const list = await getNewsLikers(newsId)
      if (likersRequestId.current === requestId) {
        setLikers(list)
      }
    } catch {
      if (likersRequestId.current === requestId) {
        setLikers([])
      }
    } finally {
      if (likersRequestId.current === requestId) {
        setLoadingLikers(false)
      }
    }
  }

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (busy || !newsId) return
    setBusy(true)
    try {
      const next = await toggleNewsLike(newsId)
      onChanged(newsId, next)
    } catch {
      // Mantém o estado atual; o pai pode exibir erro se quiser
    } finally {
      setBusy(false)
    }
  }

  const liked = summary.likedByMe
  const Icon = liked ? HeartSolid : HeartOutline

  const tooltipContent =
    summary.count <= 0 ? (
      liked ? 'Remover curtida' : 'Curtir'
    ) : likers === null ? (
      'Carregando…'
    ) : likers.length > 0 ? (
      <div className="space-y-1">
        <div className="font-medium">
          {summary.count === 1 ? '1 curtida' : `${summary.count} curtidas`}
        </div>
        <ul className="space-y-0.5">
          {likers.map((l) => (
            <li key={l.userId}>{l.name}</li>
          ))}
        </ul>
      </div>
    ) : (
      'Ninguém curtiu ainda'
    )

  return (
    <Tooltip
      position="top"
      delay={200}
      constrainWidth
      maxWidth={220}
      content={tooltipContent}
      className="inline-flex"
    >
      <button
        type="button"
        onClick={(e) => void handleClick(e)}
        onMouseEnter={() => void loadLikers()}
        onFocus={() => void loadLikers()}
        disabled={busy || !newsId}
        aria-pressed={liked}
        aria-label={liked ? 'Remover curtida' : 'Curtir notícia'}
        className={clsx(
          'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition disabled:opacity-50',
          liked
            ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/40'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600',
          className
        )}
      >
        <Icon className={clsx('h-4 w-4', liked && 'text-red-500')} />
        <span className="tabular-nums">{summary.count}</span>
      </button>
    </Tooltip>
  )
}

export default NewsLikeButton
